import type { AccountProps } from 'app-components/flux/accounts/accountsTypes';
import { useAppSelector } from 'hooks/redux';
import type { SyntheticEvent } from 'react';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import Stack from '@mui/material/Stack';

import { accountsSelectors } from 'app-components/flux/accounts';
import { businessSelectors } from 'app-components/flux/business';
import { categoriesSelectors } from 'app-components/flux/categories';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { createSelector } from 'reselect';

import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import CategoryField from 'components/QuickenControls/CategoryField';
import UsageSelect from 'components/UsageSelect';
import type { Map as ImmutableMap, List, RecordOf } from 'immutable';

export const getUsageSelector = createSelector(
  [
    categoriesSelectors.getCategoriesById,
    accountsSelectors.getAccountsById,
    businessSelectors.getDefaultBusiness,
    entitlementsSelectors.getBusinessEnabled,
    (_, props) => props,
  ],
  (categoriesById, accountsById, defaultBusiness, businessEnabled, props) => {
    const { accountId, coa, businessId } = props;
    if (!businessEnabled) {
      return 'PERSONAL';
    }
    if (coa?.id) {
      const category = categoriesById.get(coa.id);
      if (category) {
        const usageType = category.usageType || 'PERSONAL';
        if (usageType === 'BUSINESS') {
          if (businessId) return businessId;
          const account = accountId ? accountsById.get(accountId) : undefined;
          return account?.businessId || defaultBusiness?.id || 'PERSONAL';
        }
        return 'PERSONAL';
      }
    }

    if (businessId) {
      return businessId;
    }

    if (accountId) {
      const account = accountsById.get(accountId);
      if (account) {
        return account.usageType === 'BUSINESS' ? account.businessId || defaultBusiness?.id || 'PERSONAL' : 'PERSONAL';
      }
    }
    return 'PERSONAL';
  },
);

function UsageWithCategory({
  accountId,
  categoryProps,
  onChange,
  showCategoryOnly,
  showUsageOnly,
  usageProps,
  disabled,
  disabledPayment,
}: {
  accountId?: string | null;
  categoryProps: any;
  onChange?: (props: { usage: string; coa: ChartOfAccountProps }) => void;
  showCategoryOnly?: boolean;
  showUsageOnly?: boolean;
  usageProps: {
    value: string | null | undefined;
    disabled?: boolean;
    iconOnly?: boolean;
    showPersonal?: boolean;
    className?: string;
    cellId?: string;
  };
  disabled?: boolean | undefined;
  disabledPayment?: boolean;
}) {
  const { value: categoryFieldCoa } = categoryProps;
  const { value: usageValue } = usageProps;

  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const maxCategoryDepth = useAppSelector(
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'maxCategoryDepth') || 3,
  );
  const defaultBusiness = useSelector(businessSelectors.getDefaultBusiness);
  const accountsById: ImmutableMap<string, RecordOf<AccountProps>> = useSelector(accountsSelectors.getAccountsById);
  const arAccountIds: List<string | null | undefined> = useSelector(accountsSelectors.getAllArAccountIds);
  const categoriesById = useSelector(categoriesSelectors.getCategoriesById);
  const qbpAccountIds: List<string | null | undefined> = useSelector(accountsSelectors.getQBPAccountIds);

  const getUsage = useCallback(
    // todo move to the selector above once this release is over and its proven to work o.O
    (coa: ChartOfAccountProps, businessId: string | null) => {
      let result;

      const category = coa?.id ? categoriesById.get(coa.id) : null;

      if (category) {
        const usageType = category.usageType || 'PERSONAL';
        if (usageType === 'BUSINESS') {
          result = businessId;
          if (!result) {
            const account = accountId ? accountsById.get(accountId) : undefined;
            result = account?.businessId || defaultBusiness?.id;
          }
        } else {
          result = 'PERSONAL';
        }
      } else if (businessId) {
        result = businessId;
      } else if (qbpAccountIds.includes(coa.id)) {
        const account = accountsById.get(coa.id);
        result = account?.businessId || defaultBusiness?.id;
      } else if (accountId) {
        const account = accountsById.get(accountId);
        if (account?.usageType === 'BUSINESS') {
          result = account?.businessId || defaultBusiness?.id;
        } else {
          result = 'PERSONAL';
        }
      } else {
        result = 'PERSONAL';
      }
      if (!result) {
        // likely only happens when there is no default business
        result = 'PERSONAL';
      }
      return result;
    },
    [accountId, accountsById, qbpAccountIds, categoriesById, defaultBusiness?.id],
  );

  const handleCategoryChange = (newCoa: ChartOfAccountProps) => {
    const newUsage = getUsage(newCoa, usageValue && usageValue !== 'PERSONAL' ? usageValue : null);
    onChange?.({ coa: newCoa, usage: newUsage });
  };

  const handleUsageChange = (_event: SyntheticEvent, newUsage: string) => {
    let newCoa = categoryFieldCoa;

    const category = categoryFieldCoa ? categoriesById.get(categoryFieldCoa.id) : null;
    if (category) {
      const usageType = category.usageType || 'PERSONAL';
      const newUsageType = newUsage === 'PERSONAL' ? 'PERSONAL' : 'BUSINESS';
      if (usageType !== newUsageType) {
        // TODO: Try and map existing category -> known -> new category
        newCoa = { type: 'UNCATEGORIZED', id: '0' };
      }
    } else {
      // If current category is a transfer to a business only account and usage is being changed to business, then
      // we should change personal category to uncategorized
      //
      const isTrnToBusinessOnlyAccount = categoryFieldCoa && qbpAccountIds.includes(categoryFieldCoa.id);
      if (isTrnToBusinessOnlyAccount && newUsage === 'PERSONAL') {
        newCoa = { type: 'UNCATEGORIZED', id: '0' };
      }
    }

    onChange?.({ coa: newCoa, usage: newUsage });
  };

  if (showCategoryOnly) {
    return <CategoryField {...categoryProps} onChange={handleCategoryChange} />;
  }
  if (showUsageOnly) {
    return <UsageSelect {...usageProps} onChange={handleUsageChange} />;
  }
  return (
    <Stack
      direction='row'
      sx={{
        '.MuiFormControl-root': {
          margin: 0,
        },
      }}
    >
      {businessEnabled && (
        <UsageSelect
          {...usageProps}
          onChange={handleUsageChange}
          slotProps={{
            InputProps: {
              sx: {
                borderTopRightRadius: '0px',
                borderBottomRightRadius: '0px',
                borderRightStyle: 'none',
              },
            },
          }}
          disabled={disabled}
        />
      )}
      <CategoryField
        {...categoryProps}
        sx={
          businessEnabled
            ? {
                borderTopLeftRadius: '0px',
                borderBottomLeftRadius: '0px',
              }
            : {}
        }
        filterFn={
          categoryProps?.filterFn
            ? categoryProps.filterFn
            : disabledPayment
              ? (coa: ChartOfAccountProps) => coa && !(coa.type === 'ACCOUNT' && arAccountIds.includes(coa.id))
              : undefined
        }
        onChange={handleCategoryChange}
        margin='normal'
        disabled={disabled}
        maxCategoryDepth={maxCategoryDepth}
      />
    </Stack>
  );
}

export default UsageWithCategory;
